import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  approveInstitution,
  createInstitution,
  listPendingInstitutions,
  rejectInstitution,
  searchSimilarInstitutions,
  type PendingInstitution,
  type SimilarInstitution,
} from "@/lib/admin.institutions.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/admin/institutions")({
  component: AdminInstitutionsPage,
});

function AdminInstitutionsPage() {
  const [includeAll, setIncludeAll] = useState(false);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const pageSize = 100;
  const listFn = useServerFn(listPendingInstitutions);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-institutions", includeAll, page],
    queryFn: () =>
      listFn({ data: { includeAll, limit: pageSize, offset: page * pageSize } }),
  });

  const focusRow = (id: string) => {
    setIncludeAll(true);
    setPage(0);
    setHighlightId(id);
    setTimeout(() => {
      document.getElementById(`inst-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
    setTimeout(() => setHighlightId(null), 2500);
  };

  return (
    <div className="space-y-8">
      <AddInstitutionForm onJumpTo={focusRow} />

      <div>
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <p className="overline">Institutions</p>
            <h2 className="font-serif text-2xl text-primary mt-1">
              {includeAll ? "All institutions" : "Pending approval"}
            </h2>
            {data && (
              <p className="text-xs font-mono text-ink-tertiary mt-1">
                {data.institutions.length} of {data.total}
              </p>
            )}
          </div>
          <label className="text-xs font-mono text-ink-tertiary flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeAll}
              onChange={(e) => {
                setIncludeAll(e.target.checked);
                setPage(0);
              }}
            />
            Show approved too
          </label>
        </div>

        {isLoading && <p className="text-ink-tertiary">Loading…</p>}
        {data && data.institutions.length === 0 && (
          <p className="text-ink-tertiary text-sm">Nothing to review.</p>
        )}

        <div className="divide-y divide-border border border-border bg-card">
          {data?.institutions.map((inst) => (
            <InstitutionRow key={inst.id} inst={inst} highlight={highlightId === inst.id} />
          ))}
        </div>

        {data && data.total > pageSize && (
          <div className="flex items-center justify-between text-xs font-mono text-ink-tertiary mt-4">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 border border-border disabled:opacity-40"
            >
              ← Prev
            </button>
            <span>
              Page {page + 1} of {Math.max(1, Math.ceil(data.total / pageSize))}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={(page + 1) * pageSize >= data.total}
              className="px-3 py-1.5 border border-border disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function useDebounced<T>(value: T, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function AddInstitutionForm({ onJumpTo }: { onJumpTo: (id: string) => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [domain, setDomain] = useState("");
  const nameInputRef = useRef<HTMLInputElement>(null);

  const debouncedName = useDebounced(name.trim(), 250);
  const similarFn = useServerFn(searchSimilarInstitutions);
  const { data: sim } = useQuery({
    queryKey: ["admin-inst-similar", debouncedName],
    queryFn: () => similarFn({ data: { q: debouncedName } }),
    enabled: debouncedName.length >= 3,
    staleTime: 30_000,
  });

  const strongMatches = (sim?.matches ?? []).filter((m) => m.score >= 0.45);
  const hasSuggestions = strongMatches.length > 0;

  const createFn = useServerFn(createInstitution);
  const create = useMutation({
    mutationFn: () =>
      createFn({
        data: {
          name: name.trim(),
          country: country.trim() || null,
          domain: domain.trim() || null,
        },
      }),
    onSuccess: (res) => {
      toast.success(`Added ${res.institution.name}`);
      qc.invalidateQueries({ queryKey: ["admin-institutions"] });
      const newId = res.institution.id;
      setName("");
      setCountry("");
      setDomain("");
      nameInputRef.current?.focus();
      onJumpTo(newId);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const canSubmit = name.trim().length > 0 && !create.isPending;

  return (
    <section className="border border-border bg-card p-5">
      <p className="overline mb-1">Add an institution</p>
      <p className="text-xs text-ink-tertiary mb-4">
        New entries are added as <span className="font-mono">approved</span>. We&apos;ll check for
        likely duplicates as you type.
      </p>

      <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1.2fr]">
        <Input
          ref={nameInputRef}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name (e.g. Oxford Brookes University)"
          maxLength={255}
        />
        <Input
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          placeholder="Country"
          maxLength={120}
        />
        <Input
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="Domain (optional, e.g. brookes.ac.uk)"
          maxLength={120}
        />
      </div>

      {hasSuggestions && (
        <div className="mt-4 border border-amber-500/40 bg-amber-50/40 dark:bg-amber-950/20 p-3">
          <p className="text-sm font-serif text-primary mb-2">
            Did you mean one of these?
          </p>
          <ul className="divide-y divide-border">
            {strongMatches.map((m) => (
              <SuggestionRow key={m.id} match={m} onUse={() => onJumpTo(m.id)} />
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4">
        {(name || country || domain) && (
          <Button
            variant="ghost"
            onClick={() => { setName(""); setCountry(""); setDomain(""); }}
            disabled={create.isPending}
          >
            Cancel
          </Button>
        )}
        <Button onClick={() => create.mutate()} disabled={!canSubmit}>
          {create.isPending ? "Adding…" : hasSuggestions ? "Add anyway" : "Add"}
        </Button>
      </div>
    </section>
  );
}

function SuggestionRow({ match, onUse }: { match: SimilarInstitution; onUse: () => void }) {
  const pct = Math.round(match.score * 100);
  return (
    <li className="py-2 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm text-primary truncate">
          {match.name}
          {match.country ? <span className="text-ink-tertiary"> · {match.country}</span> : null}
        </p>
        <p className="text-[0.7rem] font-mono text-ink-tertiary">
          {pct}% match · {match.permitted ? "approved" : "pending"}
          {match.domain ? ` · ${match.domain}` : ""}
        </p>
      </div>
      <Button size="sm" variant="outline" onClick={onUse}>
        Use this instead
      </Button>
    </li>
  );
}

function InstitutionRow({
  inst,
  highlight,
}: {
  inst: PendingInstitution;
  highlight: boolean;
}) {
  const qc = useQueryClient();
  const approveFn = useServerFn(approveInstitution);
  const rejectFn = useServerFn(rejectInstitution);
  const [name, setName] = useState(inst.name);
  const [country, setCountry] = useState(inst.country ?? "");
  const [domain, setDomain] = useState(inst.domain ?? "");

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin-institutions"] });

  const approve = useMutation({
    mutationFn: (vars: { confirmDuplicate?: boolean } = {}) =>
      approveFn({
        data: {
          id: inst.id,
          name: name.trim() || inst.name,
          country: country.trim() ? country.trim() : null,
          domain: domain.trim() ? domain.trim() : null,
          confirmDuplicate: vars.confirmDuplicate,
        },
      }),
    onSuccess: (res) => {
      if (!res.ok && res.needsDuplicateConfirm) {
        const names = res.conflicts.map((c) => `"${c.name}"`).join(", ");
        if (window.confirm(`Near-duplicate approved institutions exist: ${names}.\n\nApprove anyway?`)) {
          approve.mutate({ confirmDuplicate: true });
        }
        return;
      }
      toast.success(`Approved ${name}`);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const reject = useMutation({
    mutationFn: () => rejectFn({ data: { id: inst.id } }),
    onSuccess: () => {
      toast.success(`Rejected ${inst.name}`);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div
      id={`inst-${inst.id}`}
      className={`p-4 grid gap-3 sm:grid-cols-[1fr_auto] items-start transition-colors ${
        highlight ? "bg-amber-100/40 dark:bg-amber-900/20 ring-2 ring-amber-500/60" : ""
      }`}
    >
      <div className="space-y-2">
        <div className="grid sm:grid-cols-3 gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            disabled={inst.permitted}
          />
          <Input
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            placeholder="Country"
            disabled={inst.permitted}
          />
          <Input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="Domain (e.g. brookes.ac.uk)"
            disabled={inst.permitted}
          />
        </div>
        <p className="text-xs font-mono text-ink-tertiary">
          {inst.permitted ? "APPROVED" : "PENDING"} · {inst.source}
          {inst.submitted_by_email ? ` · submitted by ${inst.submitted_by_email}` : ""}
          {" · "}
          {new Date(inst.created_at).toLocaleDateString()}
        </p>
      </div>
      {!inst.permitted && (
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => approve.mutate({})}
            disabled={approve.isPending || reject.isPending}
          >
            {approve.isPending ? "Approving…" : "Approve"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => reject.mutate()}
            disabled={approve.isPending || reject.isPending}
          >
            Reject
          </Button>
        </div>
      )}
    </div>
  );
}
